import React, { useMemo, useState } from "react";
import {
  Accordion,
  Box,
  Button,
  Flex,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconChevronDown, IconSearch } from "@tabler/icons-react";
import { Trans, useTranslation } from "react-i18next";
import {
  ITPurposeDoc,
  ITPurposeGroup,
  ITPurposeGroupItem,
} from "@/dto/international-transfer/international-transfer";

// Tam giác đổ xuống cho header nhóm (Accordion tự xoay 180° khi mở).
const TriangleDown = () => (
  <svg width={12} height={12} viewBox="0 0 12 12" fill="none">
    <path d="M2 4 L6 9 L10 4 Z" fill="#4B5563" />
  </svg>
);

/** Chọn tên theo ngôn ngữ (name = tiếng Việt, nameEn = tiếng Anh). */
const pickLangName = (o: { name: string; nameEn?: string }, lang?: string) =>
  (lang || "vi").startsWith("en") && o.nameEn ? o.nameEn : o.name;

/**
 * Chọn ghi chú theo ngôn ngữ. Tách riêng khỏi pickLangName vì cặp trường khác nhau
 * (note/noteEn chứ không phải name/nameEn). Thiếu noteEn thì lùi về tiếng Việt —
 * mất hẳn dòng ghi chú tệ hơn là hiện sai ngôn ngữ.
 */
const pickLangNote = (o: { note?: string; noteEn?: string }, lang?: string) =>
  (lang || "vi").startsWith("en") && o.noteEn ? o.noteEn : o.note;

/**
 * Lấy danh sách "HỒ SƠ BẮT BUỘC" của một mục đích chi tiết (VD "CUR_03") từ dữ liệu
 * getIntlTransferPurpose. BE là NGUỒN DUY NHẤT — không còn hardcode ở FE.
 */
export const getRequiredDocuments = (
  purposeGroups: ITPurposeGroup[] | undefined,
  purposeDetailCode?: string,
): ITPurposeDoc[] => {
  if (!purposeGroups || !purposeDetailCode) return [];
  const item = purposeGroups
    .flatMap((g) => g.items || [])
    .find((it) => it.code === purposeDetailCode);
  return item?.requiredDocuments ?? [];
};

const RED = "#E5484D";
const BLUE = "#239CC5";
const BOX_BG = "#F8FAFC";

const BulletItem: React.FC<{ text: string; color: string; muted?: boolean }> = ({
  text,
  color,
  muted,
}) => (
  <Box style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
    <Box
      style={{
        width: 5,
        height: 5,
        borderRadius: "50%",
        background: color,
        marginTop: 8,
        flexShrink: 0,
      }}
    />
    <Text fz={14} c={muted ? "#6B7280" : "#374151"}>
      {text}
    </Text>
  </Box>
);

const SubSectionView: React.FC<{
  item: ITPurposeGroupItem;
  lang?: string;
  isLast?: boolean;
}> = ({ item, lang, isLast }) => {
  const { t } = useTranslation();
  const required = item.requiredDocuments || [];
  const optional = item.optionalDocuments || [];
  return (
    <Box
      px={16}
      pb={18}
      mb={isLast ? 0 : 18}
      style={isLast ? undefined : { borderBottom: "1px solid #EEF1F5" }}
    >
      {/* Tiêu đề mục con — nền trắng */}
      <Text fw={600} fz={15} c="#111827" mb={10}>
        {pickLangName(item, lang)}
      </Text>
      {/* Box HỒ SƠ BẮT BUỘC */}
      {!!required.length && (
        <Box style={{ background: BOX_BG, borderRadius: 10, padding: "12px 16px" }}>
          <Text fz={12} fw={700} c={RED} mb={8} style={{ letterSpacing: 0.3 }}>
            ● {t("INTL_DOC_REQUIRED_UPPER")}
          </Text>
          <Stack spacing={6}>
            {required.map((r) => (
              <BulletItem
                key={r.code || r.name}
                text={pickLangName(r, lang)}
                color={RED}
              />
            ))}
          </Stack>
        </Box>
      )}
      {/* Box HỒ SƠ TÙY CHỌN */}
      {!!optional.length && (
        <Box
          style={{
            background: BOX_BG,
            borderRadius: 10,
            padding: "12px 16px",
            marginTop: 10,
          }}
        >
          <Text fz={12} fw={700} c={BLUE} mb={8} style={{ letterSpacing: 0.3 }}>
            ○ {t("OPTIONAL_DOCUMENTS_UPPER")}
          </Text>
          <Stack spacing={6}>
            {optional.map((o) => (
              <BulletItem
                key={o.code || o.name}
                text={pickLangName(o, lang)}
                color="#9CA3AF"
                muted
              />
            ))}
          </Stack>
        </Box>
      )}
      {/* Note vàng */}
      {pickLangNote(item, lang) && (
        <Box
          style={{
            marginTop: 10,
            background: "#FFFBEB",
            borderLeft: "3px solid #F59E0B",
            borderRadius: 6,
            padding: "8px 12px",
          }}
        >
          <Text fz={13} c="#92610A">
            📎 {pickLangNote(item, lang)}
          </Text>
        </Box>
      )}
    </Box>
  );
};

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Danh mục mục đích + hồ sơ từ getIntlTransferPurpose (BE là nguồn duy nhất). */
  purposeGroups: ITPurposeGroup[];
}

const DocumentChecklistModal: React.FC<Props> = ({
  opened,
  onClose,
  purposeGroups,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState<string>("ALL");

  const groupOptions = useMemo(
    () => [
      { value: "ALL", label: `${t("ALL_GROUPS")}` },
      ...purposeGroups.map((g) => ({ value: g.code, label: pickLangName(g, lang) })),
    ],
    [purposeGroups, lang, t],
  );

  const filtered = useMemo(() => {
    const kw = search.trim().toLowerCase();
    return purposeGroups
      .filter((g) => groupFilter === "ALL" || g.code === groupFilter)
      .map((g) => ({
        ...g,
        items: (g.items || []).filter((it) => {
          if (!kw) return true;
          const docs = [
            ...(it.requiredDocuments || []),
            ...(it.optionalDocuments || []),
          ].map((d) => pickLangName(d, lang));
          const hay = [pickLangName(it, lang), ...docs].join(" ").toLowerCase();
          return hay.includes(kw);
        }),
      }))
      .filter((g) => (g.items || []).length > 0);
  }, [search, groupFilter, purposeGroups, lang]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={760}
      radius={16}
      centered
      title={
        <Box>
          <Text fw={700} fz={18} c="#111827">
            {t("INTL_TRANSFER_DOC_CHECKLIST_TITLE")}
          </Text>
          <Text fz={13} c="#6B7280">
            {t("INTL_TRANSFER_DOC_CHECKLIST_SUBTITLE")}
          </Text>
        </Box>
      }
      styles={{
        header: { alignItems: "flex-start" },
        body: { paddingTop: 8 },
      }}
    >
      <Stack spacing={16}>
        <Box style={{ display: "flex", gap: 12 }}>
          <TextInput
            style={{ flex: 1 }}
            radius={8}
            icon={<IconSearch size={16} />}
            placeholder={`${t("SEARCH_PURPOSE_OR_DOC_TYPE")}`}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <Select
            w={160}
            radius={8}
            data={groupOptions}
            value={groupFilter}
            onChange={(v) => setGroupFilter(v || "ALL")}
            rightSection={<IconChevronDown size={16} />}
          />
        </Box>

        <Box
          style={{
            background: "#EFF6FF",
            borderRadius: 8,
            padding: "10px 14px",
          }}
        >
          <Text fz={13} c="#1E40AF">
            <Trans
              i18nKey="INTL_DOC_CHECKLIST_NOTE_HTML"
              components={{ b: <b /> }}
            />
          </Text>
        </Box>

        {filtered.length === 0 ? (
          <Box style={{ padding: "24px 0", textAlign: "center" }}>
            <Text fz={14} c="#6B7280">
              {t("NO_DOC_CHECKLIST_DATA")}
            </Text>
          </Box>
        ) : (
          <Box style={{ maxHeight: "56vh", overflowY: "auto" }}>
            <Accordion
              multiple
              defaultValue={purposeGroups.map((g) => g.code)}
              chevron={<TriangleDown />}
              styles={{
                control: {
                  background: "#fff",
                  borderBottom: "1px solid #EAEEF2",
                  borderRadius: 0,
                  paddingTop: 12,
                  paddingBottom: 12,
                  paddingLeft: 16,
                  paddingRight: 16,
                },
                label: { fontWeight: 700, fontSize: 15, color: "#111827" },
                item: {
                  border: "1px solid #E9EDF2",
                  borderRadius: 12,
                  marginBottom: 12,
                  overflow: "hidden",
                  background: "#fff",
                },
                // Bỏ padding ngang để gạch ngăn mục con chạy full chiều rộng card.
                content: { padding: 0, paddingTop: 14 },
              }}
            >
              {filtered.map((g) => (
                <Accordion.Item key={g.code} value={g.code}>
                  <Accordion.Control>{pickLangName(g, lang)}</Accordion.Control>
                  <Accordion.Panel>
                    {(g.items || []).map((it, i) => (
                      <SubSectionView
                        key={it.code}
                        item={it}
                        lang={lang}
                        isLast={i === (g.items || []).length - 1}
                      />
                    ))}
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Box>
        )}

        {/* Footer */}
        <Flex
          justify="space-between"
          align="center"
          pt={14}
          style={{ borderTop: "1px solid #EEF1F5" }}
        >
          {/* #55992 (mở lại 18/09/2026): dòng chân modal vẫn viết cứng tiếng Việt —
              tester chỉ đích danh chỗ này. */}
          <Text fz={13} c="#6B7280">
            {t("UNDER_CIRCULAR_20_AND_12_2022_SBV")}
          </Text>
          <Button
            variant="outline"
            radius={8}
            onClick={onClose}
            sx={() => ({
              borderColor: "#D0D5DD",
              color: "#344054",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#F9FAFB" },
            })}
          >
            {t("CLOSE")}
          </Button>
        </Flex>
      </Stack>
    </Modal>
  );
};

export default DocumentChecklistModal;
